import { type ComponentType, type ElementType, type FC, forwardRef } from 'react'
import { useIntl } from 'react-intl'
import { css } from '@emotion/react'
import {
  Button as MaterialButton,
  type ButtonTypeMap as MaterialButtonTypeMap,
  type ExtendButtonBase,
} from '@mui/material'
import { type OverrideProps } from '@mui/material/OverridableComponent'
import { useTheme } from '@mui/material/styles'

import Loader from '@DS/components/components/loaders/loader/Loader'
import { getInnerText } from '@Utils/html'

import { type ButtonSize, type ButtonVariant } from './Buttons.types'
import * as styles from './styles'

/**
 * Props à ajouter à celles de base du composant Material
 */
type IncludingMaterialButtonProps = {
  loading?: boolean
  icon?: ComponentType
  isExternal?: boolean
  size?: ButtonSize
}
/**
 * Props à exclure de celles de base du composant Material, car elles seraient ignorées
 */
type ExcludingMaterialButtonProps = 'variant' | 'color' | 'startIcon' | 'disableElevation' | 'disabled'
/**
 * Props existant déjà dans le composant Material qu'on veut surcharger
 */
type OverridingMaterialButtonProps = {
  variant?: ButtonVariant
  // On ne permet pas que ce composant DS soit "disabled", voir avec
  // (Grégory PUJOL)[gregory_pujol@connect-tech.sncf] si besoin de lever la restriction
  disabled?: false
}
/**
 * "Re-build" des props de base du composant Material dans le but de les "customiser"
 */
type FixedMaterialButtonProps<ButtonComponentType extends ElementType> = MaterialButtonTypeMap<
  IncludingMaterialButtonProps,
  ButtonComponentType
>
/**
 * Type utilitaire qui servira à "builder" les props du composant actuel via le système d'extension interne de
 * Material, en "customisant" au passage les props de base du composant Material
 */
type ButtonTypeMap<ButtonComponentType extends ElementType = MaterialButtonTypeMap['defaultComponent']> = Omit<
  FixedMaterialButtonProps<ButtonComponentType>,
  'props'
> & {
  props: Omit<FixedMaterialButtonProps<ButtonComponentType>['props'], ExcludingMaterialButtonProps> &
    OverridingMaterialButtonProps
}

/**
 * Type final des props du composant actuel, "buildé" depuis le système d'extension interne de Material
 */
export type ButtonProps<ButtonComponentType extends ElementType = MaterialButtonTypeMap['defaultComponent']> =
  OverrideProps<ButtonTypeMap<ButtonComponentType>, ButtonComponentType> & {
    component?: ButtonComponentType
  }

/**
 * On ne peut pas utiliser directement le type `ExtendButtonBase<ButtonTypeMap>` car TS n'arrive pas à gérer
 * l'"overloading" à ce niveau et génère une erreur de compatibilité
 */
const Button: FC<ButtonProps> = forwardRef(
  (
    {
      children,
      loading = false,
      icon: Icon,
      variant = 'primary',
      disableRipple = false,
      isExternal = false,
      title,
      size = 'medium',
      ...rest
    },
    ref,
  ) => {
    const theme = useTheme()
    const { formatMessage } = useIntl()

    if (loading) {
      return (
        <div
          css={css([
            styles.loadingWrapper,
            rest.fullWidth && styles.fullWidth,
            size === 'small' && styles.smallButton(theme),
          ])}
          className={rest.className}
        >
          <Loader size={32} />
        </div>
      )
    }

    const linkTitle = getInnerText(children)

    return (
      <MaterialButton
        ref={ref}
        {...rest}
        disableFocusRipple
        disableRipple={disableRipple}
        startIcon={Icon && <Icon css={styles.buttonIcon(size)} />}
        variant={variant === 'primary' ? 'contained' : 'outlined'}
        color="primary"
        css={[styles.button(theme, variant), size === 'small' && styles.smallButton(theme)]}
        title={
          title || (isExternal && linkTitle)
            ? `${linkTitle}${formatMessage({ id: 'link_newTab_titleSuffix' })}`
            : undefined
        }
        {...(isExternal &&
          !!rest.href &&
          ({
            target: '_blank',
            rel: 'noopener noreferrer',
          } satisfies ButtonProps<'a'>))}
      >
        {children}
      </MaterialButton>
    )
  },
)

/**
 * Cast du type du composant pour une meilleure intégration à l'utilisation
 */
export default Button as ExtendButtonBase<ButtonTypeMap>
